// Discord-style full-viewport User Settings overlay (batch 7.5, item 2).
// - Two-column layout: left sidebar (nav) + right pane (section content).
// - Sections: My Account, Profile, Privacy & Safety, Connections, Appearance,
//   Notifications, Voice & Video, Keybinds, What's New, Log Out.
// - Trigger: settings gear on user bar OR Ctrl/Cmd+, keyboard shortcut.
// - Esc closes. Click outside does NOT close (Discord pattern).
// - Mobile: becomes full-screen with a toggle for the nav column.

import { state } from '../state';
import { get, post } from '../api';
import { showToast } from './toast';
import { confirmModal, promptModal } from './modal';
import { iconX, iconUsers, iconEdit, iconShield, iconLink, iconSettings, iconBell, iconKey, iconLogOut, iconCopy, iconSun, iconMoon, iconCheck, iconMenu, iconCalendar, iconTrash, iconSliders, iconGlobe, iconAccessibility, iconTool } from '../icons';
import { requestNotificationPermissionSync } from './notifications';
import { isNtfySubscribed, subscribeNtfy, unsubscribeNtfy } from './ntfy-subscribe';
import { renderSecuritySettings } from '../security-settings';
import type { FeatureFlag } from '../types';

type SectionKey =
  | 'account' | 'profile' | 'privacy' | 'security' | 'connections'
  | 'voice' | 'notifications' | 'appearance'
  | 'accessibility' | 'language' | 'advanced'
  | 'keybinds' | 'flags' | 'bookings' | 'whatsnew' | 'logout';

interface SectionDef { key: SectionKey; label: string; icon: () => string; group: 'user' | 'app' | 'hub' | 'meta'; }

// Discord order: My Account, Profiles, Privacy & Safety, Connections,
// Voice & Video, Notifications, Appearance, Accessibility, Language, Advanced.
// Hub-specific (Bookings / Keybinds / Feature Flags / What's New) grouped below.
const SECTIONS: SectionDef[] = [
  { key: 'account',        label: 'My Account',        icon: iconUsers,        group: 'user' },
  { key: 'profile',        label: 'Profiles',          icon: iconEdit,         group: 'user' },
  { key: 'privacy',        label: 'Privacy & Safety',  icon: iconShield,       group: 'user' },
  { key: 'security',       label: 'Security',          icon: iconKey,          group: 'user' },
  { key: 'connections',    label: 'Connections',       icon: iconLink,         group: 'user' },
  { key: 'voice',          label: 'Voice & Video',     icon: iconSettings,     group: 'app' },
  { key: 'notifications',  label: 'Notifications',     icon: iconBell,         group: 'app' },
  { key: 'appearance',     label: 'Appearance',        icon: iconSun,          group: 'app' },
  { key: 'accessibility',  label: 'Accessibility',     icon: iconAccessibility,group: 'app' },
  { key: 'language',       label: 'Language',          icon: iconGlobe,        group: 'app' },
  { key: 'advanced',       label: 'Advanced',          icon: iconTool,         group: 'app' },
  { key: 'bookings',       label: 'Bookings',          icon: iconCalendar,     group: 'hub' },
  { key: 'keybinds',       label: 'Keybinds',          icon: iconKey,          group: 'hub' },
  { key: 'flags',          label: 'Feature Flags',     icon: iconSliders,      group: 'hub' },
  { key: 'whatsnew',       label: "What's New",        icon: iconBell,         group: 'meta' },
  { key: 'logout',         label: 'Log Out',           icon: iconLogOut,       group: 'meta' },
];

let overlayEl: HTMLElement | null = null;
let currentSection: SectionKey = 'account';
let escHandler: ((e: KeyboardEvent) => void) | null = null;

/** Open the overlay; optionally jump directly to a section. */
export function openUserSettings(initial?: SectionKey): void {
  if (initial) currentSection = initial;
  if (overlayEl) { renderOverlay(); return; }

  overlayEl = document.createElement('div');
  overlayEl.className = 'us-overlay';
  overlayEl.setAttribute('role', 'dialog');
  overlayEl.setAttribute('aria-modal', 'true');
  overlayEl.setAttribute('aria-label', 'User Settings');
  document.body.appendChild(overlayEl);
  document.body.classList.add('us-open');

  renderOverlay();

  escHandler = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') { e.preventDefault(); closeUserSettings(); return; }
    // Discord parity — Alt+letter jumps to the matching nav section. We skip
    // when other modifiers are held (Ctrl/Meta) so we don't shadow browser /
    // OS shortcuts. Letters are normalised to lowercase for the lookup.
    if (e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey && e.key.length === 1) {
      const ch = e.key.toLowerCase();
      const target = accelMap.get(ch);
      if (target) {
        e.preventDefault();
        if (target === 'logout') { handleLogout(); return; }
        stopMicTest();
        stopCamTest();
        currentSection = target;
        renderOverlay();
      }
    }
  };
  document.addEventListener('keydown', escHandler);
}

export function closeUserSettings(): void {
  if (!overlayEl) return;
  stopMicTest();
  stopCamTest();
  overlayEl.remove();
  overlayEl = null;
  document.body.classList.remove('us-open');
  if (escHandler) { document.removeEventListener('keydown', escHandler); escHandler = null; }
  flagsLoaded = false;
  flagsCache = [];
  bookingsCache = { me: null, availability: [], blackouts: [], bookings: [], loaded: false };
}

function renderOverlay(): void {
  if (!overlayEl) return;
  overlayEl.innerHTML = `
    <div class="us-shell" role="document">
      <button class="us-nav-toggle" id="us-nav-toggle" type="button" aria-label="Toggle navigation" title="Toggle navigation">${iconMenu()}</button>
      <aside class="us-sidebar" id="us-sidebar">
        ${renderNav()}
      </aside>
      <main class="us-main">
        <div class="us-topbar">
          <h1 class="us-title" id="us-title">${sectionTitle(currentSection)}</h1>
          <div class="us-close-wrap">
            <button class="us-close" id="us-close" type="button" aria-label="Close settings (Esc)" title="Close (Esc)">${iconX()}</button>
            <span class="us-esc-hint" aria-hidden="true">ESC</span>
          </div>
        </div>
        <div class="us-body" id="us-body">${renderSection(currentSection)}</div>
      </main>
    </div>
  `;

  // Nav click
  overlayEl.querySelectorAll<HTMLElement>('.us-nav-item').forEach(el => {
    el.addEventListener('click', () => {
      const key = el.dataset['section'] as SectionKey | undefined;
      if (!key) return;
      if (key === 'logout') { handleLogout(); return; }
      stopMicTest();
      stopCamTest();
      currentSection = key;
      renderOverlay();
      // Close the mobile nav after navigating
      overlayEl?.classList.remove('us-nav-open');
    });
  });

  // Close button
  document.getElementById('us-close')?.addEventListener('click', closeUserSettings);

  // Mobile nav toggle
  document.getElementById('us-nav-toggle')?.addEventListener('click', () => {
    overlayEl?.classList.toggle('us-nav-open');
  });

  // Section-specific wiring
  wireSection(currentSection);
}

/**
 * Pick a unique accelerator letter per nav label. We try the first letter
 * first, then walk the label until we find one that hasn't been claimed.
 * Returns the lowercase letter so the keydown handler can match against it.
 */
function pickAccelerator(label: string, used: Set<string>): string | null {
  const lower = label.toLowerCase();
  for (let i = 0; i < lower.length; i++) {
    const ch = lower.charAt(i);
    if (ch >= 'a' && ch <= 'z' && !used.has(ch)) {
      used.add(ch);
      return ch;
    }
  }
  return null;
}

/** Render the label with the accelerator letter wrapped in <u>...</u>. */
function renderAcceleratedLabel(label: string, accel: string | null): string {
  if (!accel) return esc(label);
  const lower = label.toLowerCase();
  const idx = lower.indexOf(accel);
  if (idx < 0) return esc(label);
  return esc(label.slice(0, idx)) + '<u>' + esc(label.charAt(idx)) + '</u>' + esc(label.slice(idx + 1));
}

const accelMap = new Map<string, SectionKey>();

function renderNav(): string {
  const groups: Array<{ title: string; group: 'user' | 'app' | 'hub' | 'meta' }> = [
    { title: 'User Settings', group: 'user' },
    { title: 'App Settings',  group: 'app' },
    { title: 'Hub',           group: 'hub' },
    { title: '',              group: 'meta' },
  ];
  const admin = isSettingsAdmin();
  // Reset + rebuild so the underline + handler stay in sync across re-renders.
  accelMap.clear();
  const used = new Set<string>();
  return groups.map(g => {
    const items = SECTIONS.filter(s => s.group === g.group && (s.key !== 'flags' || admin));
    const title = g.title ? `<div class="us-nav-title">${esc(g.title)}</div>` : '<div class="us-nav-separator"></div>';
    const itemsHtml = items.map(s => {
      const active = s.key === currentSection ? ' us-nav-active' : '';
      const danger = s.key === 'logout' ? ' us-nav-danger' : '';
      const accel = pickAccelerator(s.label, used);
      if (accel) accelMap.set(accel, s.key);
      const labelHtml = renderAcceleratedLabel(s.label, accel);
      const accelAttr = accel ? ` accesskey="${accel}" title="Alt+${accel.toUpperCase()}"` : '';
      return `<button class="us-nav-item${active}${danger}" data-section="${s.key}" type="button"${accelAttr}>
        <span class="us-nav-icon">${s.icon()}</span>
        <span class="us-nav-label">${labelHtml}</span>
      </button>`;
    }).join('');
    return title + itemsHtml;
  }).join('');
}

function sectionTitle(key: SectionKey): string {
  return SECTIONS.find(s => s.key === key)?.label || 'Settings';
}

function renderSection(key: SectionKey): string {
  switch (key) {
    case 'account':       return renderAccount();
    case 'profile':        return renderProfile();
    case 'privacy':        return renderPrivacy();
    case 'security':       return '<div id="us-security-mount"></div>';
    case 'connections':    return renderConnections();
    case 'appearance':     return renderAppearance();
    case 'notifications':  return renderNotifications();
    case 'voice':          return renderVoice();
    case 'keybinds':       return renderKeybinds();
    case 'flags':          return renderFlags();
    case 'bookings':       return renderBookings();
    case 'whatsnew':       return renderWhatsNew();
    case 'accessibility':  return renderAccessibility();
    case 'language':       return renderLanguage();
    case 'advanced':       return renderAdvanced();
    default:               return '';
  }
}

function wireSection(key: SectionKey): void {
  switch (key) {
    case 'account':       wireAccount(); break;
    case 'profile':        wireProfile(); break;
    case 'security':       wireSecurity(); break;
    case 'appearance':     wireAppearance(); break;
    case 'voice':          wireVoice(); break;
    case 'notifications':  wireNotifications(); break;
    case 'flags':          wireFlags(); break;
    case 'bookings':       wireBookings(); break;
    case 'accessibility':  wireAccessibility(); break;
    case 'language':       wireLanguage(); break;
    case 'advanced':       wireAdvanced(); break;
    default: break;
  }
}

function wireAccessibility(): void {
  document.getElementById('us-ac-reduced')?.addEventListener('change', (e) => {
    const on = (e.currentTarget as HTMLInputElement).checked;
    localStorage.setItem('hub-people-reduced-motion', String(on));
    document.documentElement.classList.toggle('reduced-motion', on);
  });
  document.getElementById('us-ac-contrast')?.addEventListener('change', (e) => {
    const on = (e.currentTarget as HTMLInputElement).checked;
    localStorage.setItem('hub-people-high-contrast', String(on));
    document.documentElement.classList.toggle('high-contrast', on);
  });
}

function wireLanguage(): void {
  document.getElementById('us-lang')?.addEventListener('change', (e) => {
    const v = (e.currentTarget as HTMLSelectElement).value;
    localStorage.setItem('hub-people-language', v);
  });
}

function wireAdvanced(): void {
  document.getElementById('us-adv-dev')?.addEventListener('change', (e) => {
    const on = (e.currentTarget as HTMLInputElement).checked;
    localStorage.setItem('hub-people-developer-mode', String(on));
  });
}

// ─── My Account (Discord 1:1) ───
function renderAccount(): string {
  const owner = state.ownerId ? state.people.find(p => p.id === state.ownerId) : null;
  const name = owner?.name || '';
  const email = owner?.email || '';
  const phone = owner?.phone || '';
  const avatar = owner?.avatar || '';
  const bannerColor = localStorage.getItem('hub-people-banner-color') || 'var(--accent)';

  const initial = (name || 'U').charAt(0).toUpperCase();
  const avatarInner = avatar
    ? `<img src="${escAttr(avatar)}" alt="avatar" class="us-account-avatar-img">`
    : `<span class="us-account-avatar-initial">${esc(initial)}</span>`;

  const row = (id: string, label: string, value: string, placeholder: string): string => `
    <div class="us-account-row" data-field="${id}">
      <div class="us-account-row-info">
        <div class="us-account-row-label">${esc(label)}</div>
        <div class="us-account-row-value" id="us-a-val-${id}">${value ? esc(value) : `<span class="us-hint-inline">${esc(placeholder)}</span>`}</div>
      </div>
      <button class="us-btn us-btn-secondary us-btn-sm" data-edit-field="${id}" type="button">Edit</button>
    </div>`;

  return `
    <section class="us-section">
      <div class="us-account-card">
        <div class="us-account-banner"${bannerColor.startsWith('var(') ? '' : ` style="background-color:${escAttr(bannerColor)}"`}></div>
        <div class="us-account-header">
          <div class="us-account-avatar">${avatarInner}</div>
          <div class="us-account-header-name">${esc(name || 'Member')}</div>
          <button class="us-btn us-btn-primary us-btn-sm" id="us-a-edit-profile" type="button">${iconEdit()} Edit User Profile</button>
        </div>
        <div class="us-account-rows">
          ${row('name', 'Display Name', name, 'Set a display name')}
          ${row('email', 'Email', email, 'no email on file')}
          ${row('phone', 'Phone', phone, 'no phone on file')}
        </div>
      </div>

      <h3 class="us-subsection-title">Password and Authentication</h3>
      <div class="us-account-rows">
        <button class="us-btn us-btn-primary us-btn-sm" id="us-a-change-password" type="button">Change Password</button>
        <div class="us-hint">Hub identity is managed upstream — password changes go through your Hub SSO.</div>
      </div>
      <div class="us-account-rows" style="margin-top:12px">
        <div class="us-account-row-label" style="margin-bottom:4px">Two-Factor Authentication</div>
        <button class="us-btn us-btn-secondary us-btn-sm" id="us-a-2fa" type="button">Enable Authenticator App</button>
        <div class="us-hint">Coming soon — 2FA enrollment is not yet wired.</div>
      </div>

      <h3 class="us-subsection-title us-subsection-danger">Account Removal</h3>
      <div class="us-account-rows">
        <div class="us-hint">Disabling your account means you can recover it at any time after taking this action.</div>
        <div style="display:flex;gap:8px;margin-top:8px;flex-wrap:wrap">
          <button class="us-btn us-btn-danger-outline us-btn-sm" id="us-a-disable" type="button">Disable Account</button>
          <button class="us-btn us-btn-danger us-btn-sm" id="us-a-delete" type="button">Delete Account</button>
        </div>
      </div>
    </section>`;
}

function wireAccount(): void {
  if (!overlayEl) return;

  const owner = state.ownerId ? state.people.find(p => p.id === state.ownerId) : null;

  // "Edit User Profile" jumps to the Profiles section
  document.getElementById('us-a-edit-profile')?.addEventListener('click', () => {
    currentSection = 'profile';
    renderOverlay();
  });

  // Inline Edit buttons — prompt for new value and persist via /api/people/update
  overlayEl.querySelectorAll<HTMLButtonElement>('[data-edit-field]').forEach(btn => {
    btn.addEventListener('click', () => {
      if (!owner) { showToast('No account loaded', 'err'); return; }
      const field = btn.dataset['editField'] || '';
      const labels: Record<string, string> = { name: 'Display Name', email: 'Email', phone: 'Phone' };
      const current = field === 'name' ? owner.name : field === 'email' ? owner.email : field === 'phone' ? owner.phone : '';
      const label = labels[field] || field;
      const inputType: 'email' | 'text' = field === 'email' ? 'email' : 'text';
      promptModal(
        `Edit ${label}`,
        '',
        (next) => {
          const trimmed = next.trim();
          if (field === 'name' && !trimmed) { showToast('Name cannot be empty', 'err'); return; }

          const payload: Record<string, string> = {
            id: owner.id,
            name: owner.name,
            avatar: owner.avatar || '',
            notes: owner.notes || '',
            email: owner.email || '',
            phone: owner.phone || '',
          };
          payload[field] = trimmed;

          void post<unknown>('/api/people/update', payload).then(() => {
            if (field === 'name') owner.name = trimmed;
            else if (field === 'email') owner.email = trimmed;
            else if (field === 'phone') owner.phone = trimmed;
            showToast(`${label} updated`, 'ok');
            renderOverlay();
          }).catch(e => showToast(String(e), 'err'));
        },
        { defaultValue: current ?? '', placeholder: label, confirmLabel: 'Save', inputType }
      );
    });
  });

  document.getElementById('us-a-change-password')?.addEventListener('click', () => {
    showToast('Password changes are managed through your Hub SSO', 'warn');
  });
  document.getElementById('us-a-2fa')?.addEventListener('click', () => {
    showToast('2FA enrollment is coming soon', 'warn');
  });
  document.getElementById('us-a-disable')?.addEventListener('click', () => {
    confirmModal('Disable account', 'Disable your account? You can re-enable it later by logging in again.', () => {
      showToast('Account disable is not yet wired — contact support', 'warn');
    }, { confirmLabel: 'Disable', variant: 'danger' });
  });
  document.getElementById('us-a-delete')?.addEventListener('click', () => {
    confirmModal('Delete account', 'Delete your account? This cannot be undone.', () => {
      showToast('To delete your account, contact support', 'warn');
    }, { confirmLabel: 'Delete', variant: 'danger' });
  });
}

// ─── Profile ───
function renderProfile(): string {
  const owner = state.ownerId ? state.people.find(p => p.id === state.ownerId) : null;
  const bio = owner?.notes || '';
  const pronouns = localStorage.getItem('hub-people-pronouns') || '';
  const accentOverride = localStorage.getItem('hub-people-profile-accent') || '';
  return `
    <section class="us-section">
      <div class="us-field">
        <label class="us-label" for="us-p-pronouns">Pronouns</label>
        <input class="us-input" id="us-p-pronouns" value="${escAttr(pronouns)}" maxlength="32" placeholder="they / them" autocomplete="off">
        <div class="us-hint">Stored on this device for now.</div>
      </div>
      <div class="us-field">
        <label class="us-label" for="us-p-bio">Bio</label>
        <textarea class="us-input" id="us-p-bio" rows="5" maxlength="600" placeholder="Tell people about yourself...">${esc(bio)}</textarea>
      </div>
      <div class="us-field">
        <label class="us-label" for="us-p-accent">Profile Theme Color</label>
        <div class="us-colour-row">
          <input class="us-colour" id="us-p-accent" type="color" value="${escAttr(accentOverride || '#16a34a')}">
          <button class="us-btn us-btn-secondary" id="us-p-accent-clear" type="button">Reset</button>
          <span class="us-hint us-hint-inline">Tints your own profile card.</span>
        </div>
      </div>
      <div class="us-row-actions">
        <button class="us-btn us-btn-primary" id="us-p-save" type="button">Save Profile</button>
      </div>
    </section>`;
}

function wireProfile(): void {
  document.getElementById('us-p-save')?.addEventListener('click', () => {
    const owner = state.ownerId ? state.people.find(p => p.id === state.ownerId) : null;
    if (!owner) { showToast('No account loaded', 'err'); return; }
    const bio = (document.getElementById('us-p-bio') as HTMLTextAreaElement | null)?.value || '';
    const pronouns = (document.getElementById('us-p-pronouns') as HTMLInputElement | null)?.value.trim() || '';
    const accent = (document.getElementById('us-p-accent') as HTMLInputElement | null)?.value || '';

    if (pronouns) localStorage.setItem('hub-people-pronouns', pronouns);
    else localStorage.removeItem('hub-people-pronouns');

    if (accent) localStorage.setItem('hub-people-profile-accent', accent);

    const btn = document.getElementById('us-p-save') as HTMLButtonElement | null;
    if (btn) btn.classList.add('us-btn-saving');

    void post<unknown>('/api/people/update', {
      id: owner.id, name: owner.name, avatar: owner.avatar || '', notes: bio,
    }).then(() => {
      owner.notes = bio;
      showToast('Profile saved', 'ok');
      if (btn) btn.classList.remove('us-btn-saving');
    }).catch(e => {
      showToast(String(e), 'err');
      if (btn) btn.classList.remove('us-btn-saving');
    });
  });

  document.getElementById('us-p-accent-clear')?.addEventListener('click', () => {
    localStorage.removeItem('hub-people-profile-accent');
    const el = document.getElementById('us-p-accent') as HTMLInputElement | null;
    if (el) el.value = '#16a34a';
    showToast('Profile color reset', 'ok');
  });
}

// ─── Privacy & Safety ───
function renderPrivacy(): string {
  return `<section class="us-section">
    <div class="us-placeholder">
      <h3>Privacy & Safety</h3>
      <p class="us-hint">Direct messages and friend requests are currently open to all server members. Granular privacy controls (DM scopes, read receipts, blocklist) are coming in a later batch.</p>
    </div>
  </section>`;
}

// ─── Security (D2 — multi-auth) ───
function wireSecurity(): void {
  const mount = document.getElementById('us-security-mount');
  if (mount) void renderSecuritySettings(mount);
}

// ─── Connections ───
function renderConnections(): string {
  const owner = state.ownerId;
  const conns = owner ? state.connections.filter(c => c.from_id === owner || c.to_id === owner) : [];
  const rows = conns.length === 0
    ? '<div class="us-hint">No external connections yet.</div>'
    : conns.map(c => {
        const otherId = c.from_id === owner ? c.to_id : c.from_id;
        const other = state.people.find(p => p.id === otherId);
        const otherName = other?.name || otherId.slice(0, 8);
        return `<div class="us-conn-row">
          <div class="us-conn-kind">${esc(c.type)}</div>
          <div class="us-conn-value">${esc(otherName)}${c.notes ? ` — <span class="us-hint-inline">${esc(c.notes)}</span>` : ''}</div>
        </div>`;
      }).join('');
  return `<section class="us-section">
    <div class="us-placeholder">
      <h3>Connections</h3>
      <p class="us-hint">Link accounts that appear on your public profile. Add/remove UI is coming — for now this view is read-only.</p>
      ${rows}
    </div>
  </section>`;
}

// ─── Appearance (Discord 1:1) ───
function renderAppearance(): string {
  const theme = localStorage.getItem('hub-people-theme') || 'dark';
  const density = localStorage.getItem('hub-people-density') || (localStorage.getItem('hub-people-compact') === 'true' ? 'compact' : 'cozy');
  const fontSize = Number(localStorage.getItem('hub-people-chat-font-size') ?? '16');
  return `<section class="us-section">
    <h3 class="us-subsection-title">Theme</h3>
    <div class="us-field">
      <div class="us-theme-picker">
        <label class="us-theme-card${theme === 'dark' ? ' us-theme-active' : ''}">
          <input type="radio" name="us-theme" value="dark" ${theme === 'dark' ? 'checked' : ''}>
          <span class="us-theme-icon">${iconMoon()}</span>
          <span class="us-theme-name">Dark</span>
        </label>
        <label class="us-theme-card${theme === 'light' ? ' us-theme-active' : ''}">
          <input type="radio" name="us-theme" value="light" ${theme === 'light' ? 'checked' : ''}>
          <span class="us-theme-icon">${iconSun()}</span>
          <span class="us-theme-name">Light</span>
        </label>
        <label class="us-theme-card${theme === 'system' ? ' us-theme-active' : ''}">
          <input type="radio" name="us-theme" value="system" ${theme === 'system' ? 'checked' : ''}>
          <span class="us-theme-icon">${iconSettings()}</span>
          <span class="us-theme-name">Sync with computer</span>
        </label>
      </div>
    </div>

    <h3 class="us-subsection-title">Message Display</h3>
    <div class="us-field">
      <label class="us-label" for="us-a-font-size">Chat Font Size <span class="us-slider-value" id="us-a-font-size-val">${fontSize}px</span></label>
      <input type="range" class="us-slider" id="us-a-font-size" min="12" max="24" step="1" value="${fontSize}">
      <div class="us-hint">Adjusts the body font size for chat messages.</div>
    </div>

    <div class="us-field">
      <div class="us-density-picker">
        <label class="us-density-card${density === 'cozy' ? ' us-density-active' : ''}">
          <input type="radio" name="us-density" value="cozy" ${density === 'cozy' ? 'checked' : ''}>
          <div class="us-density-preview us-density-preview-cozy"></div>
          <div class="us-density-name">Cozy</div>
          <div class="us-density-hint">Roomy and inviting — our default.</div>
        </label>
        <label class="us-density-card${density === 'compact' ? ' us-density-active' : ''}">
          <input type="radio" name="us-density" value="compact" ${density === 'compact' ? 'checked' : ''}>
          <div class="us-density-preview us-density-preview-compact"></div>
          <div class="us-density-name">Compact</div>
          <div class="us-density-hint">Fits more messages on screen.</div>
        </label>
      </div>
    </div>
  </section>`;
}

function wireAppearance(): void {
  if (!overlayEl) return;
  overlayEl.querySelectorAll<HTMLInputElement>('input[name="us-theme"]').forEach(input => {
    input.addEventListener('change', () => {
      const v = input.value;
      if (v === 'system') {
        localStorage.removeItem('hub-people-theme');
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        document.documentElement.dataset['theme'] = prefersDark ? 'dark' : 'light';
      } else {
        localStorage.setItem('hub-people-theme', v);
        document.documentElement.dataset['theme'] = v;
      }
      renderOverlay();
    });
  });

  const font = document.getElementById('us-a-font-size') as HTMLInputElement | null;
  const fontLabel = document.getElementById('us-a-font-size-val');
  font?.addEventListener('input', () => {
    const px = font.value;
    if (fontLabel) fontLabel.textContent = `${px}px`;
    localStorage.setItem('hub-people-chat-font-size', px);
    document.documentElement.style.setProperty('--chat-font-size', `${px}px`);
  });

  overlayEl.querySelectorAll<HTMLInputElement>('input[name="us-density"]').forEach(input => {
    input.addEventListener('change', () => {
      const v = input.value;
      localStorage.setItem('hub-people-density', v);
      const compact = v === 'compact';
      localStorage.setItem('hub-people-compact', String(compact));
      document.body.classList.toggle('compact-mode', compact);
      renderOverlay();
    });
  });
}

// ─── Accessibility / Language / Advanced (stubs) ───
function renderAccessibility(): string {
  const reducedMotion = localStorage.getItem('hub-people-reduced-motion') === 'true';
  const highContrast = localStorage.getItem('hub-people-high-contrast') === 'true';
  return `<section class="us-section">
    <h3 class="us-subsection-title">Motion</h3>
    <div class="us-field">
      <label class="us-toggle-row">
        <span><span class="us-toggle-label">Reduced Motion</span>
        <span class="us-hint">Suppresses non-essential animations and transitions.</span></span>
        <input type="checkbox" id="us-ac-reduced" ${reducedMotion ? 'checked' : ''}>
      </label>
    </div>
    <h3 class="us-subsection-title">Color &amp; Contrast</h3>
    <div class="us-field">
      <label class="us-toggle-row">
        <span><span class="us-toggle-label">Increase Contrast</span>
        <span class="us-hint">Intensifies borders and text contrast.</span></span>
        <input type="checkbox" id="us-ac-contrast" ${highContrast ? 'checked' : ''}>
      </label>
    </div>
  </section>`;
}

function renderLanguage(): string {
  const lang = localStorage.getItem('hub-people-language') || 'en-AU';
  return `<section class="us-section">
    <h3 class="us-subsection-title">Display Language</h3>
    <div class="us-field">
      <label class="us-label" for="us-lang">App Language</label>
      <select class="us-input" id="us-lang">
        <option value="en-AU" ${lang === 'en-AU' ? 'selected' : ''}>English (Australia)</option>
        <option value="en-US" ${lang === 'en-US' ? 'selected' : ''}>English (United States)</option>
      </select>
      <div class="us-hint">Only English is supported right now. Additional locales will be added in a later batch.</div>
    </div>
  </section>`;
}

function renderAdvanced(): string {
  const devMode = localStorage.getItem('hub-people-developer-mode') === 'true';
  return `<section class="us-section">
    <h3 class="us-subsection-title">Developer Mode</h3>
    <div class="us-field">
      <label class="us-toggle-row">
        <span><span class="us-toggle-label">Developer Mode</span>
        <span class="us-hint">Adds "Copy ID" to right-click menus and exposes internal IDs.</span></span>
        <input type="checkbox" id="us-adv-dev" ${devMode ? 'checked' : ''}>
      </label>
    </div>
  </section>`;
}

// ─── Notifications (placeholder) ───
// ─── Notifications (Discord 1:1) ───
function renderNotifications(): string {
  const master = localStorage.getItem('hub-people-notifs-master') !== 'false';
  const sound = localStorage.getItem('hub-people-sound') !== 'false';

  const permStatus: 'granted' | 'denied' | 'default' | 'unsupported' =
    !('Notification' in window) ? 'unsupported' : Notification.permission as 'granted' | 'denied' | 'default';
  const permHint = permStatus === 'granted'
    ? 'Your browser has granted permission.'
    : permStatus === 'denied'
      ? 'Your browser has blocked notifications. Update site permissions to re-enable.'
      : permStatus === 'unsupported'
        ? "This browser doesn't support desktop notifications."
        : "Click Enable to request your browser's permission.";

  const myId = state.ownerId || state.people[0]?.id || '';
  const myServerIds = state.serverMembers
    .filter(m => m.person_id === myId)
    .map(m => m.server_id);
  const myServers = state.servers.filter(s => myServerIds.includes(s.id));

  const serverModes = (() => {
    try {
      const raw = localStorage.getItem('hub-people-server-notifs');
      if (!raw) return {} as Record<string, string>;
      return JSON.parse(raw) as Record<string, string>;
    } catch { return {} as Record<string, string>; }
  })();

  const serverRowsHtml = myServers.length === 0
    ? `<div class="us-hint">Join a server to configure per-server overrides.</div>`
    : myServers.map((s) => {
      const raw = serverModes[s.id];
      const mode = raw === 'mentions' || raw === 'none' ? raw : 'all';
      return `<div class="us-notif-server-row" data-server-id="${escAttr(s.id)}">
        <div class="us-notif-server-name">${esc(s.name)}</div>
        <select class="us-input us-notif-server-select" data-server-id="${escAttr(s.id)}">
          <option value="all" ${mode === 'all' ? 'selected' : ''}>All Messages</option>
          <option value="mentions" ${mode === 'mentions' ? 'selected' : ''}>Only @Mentions</option>
          <option value="none" ${mode === 'none' ? 'selected' : ''}>Nothing</option>
        </select>
      </div>`;
    }).join('');

  const mutedList = (() => {
    try {
      const raw = localStorage.getItem('hub-people-muted-channels');
      if (!raw) return [];
      const arr = JSON.parse(raw) as unknown;
      if (!Array.isArray(arr)) return [];
      return arr.filter((x): x is string => typeof x === 'string');
    } catch { return []; }
  })();
  const mutedHtml = mutedList.length === 0
    ? `<div class="us-hint">No channels muted.</div>`
    : mutedList.map((ch) => `<div class="us-notif-muted-row">
        <span class="us-notif-muted-name">#${esc(ch)}</span>
        <button class="us-btn us-btn-sm us-notif-unmute" type="button" data-channel="${escAttr(ch)}">Unmute</button>
      </div>`).join('');

  // GLOBAL confirmation: notifications are on for EVERY channel when the master
  // is on, no server is set to mentions/none, and nothing is muted. Categories
  // are channel groups (covered) and role-mentions ride the all-messages path,
  // so this single switch is "notify me for all channels, categories & roles".
  const allChannelsOn = master
    && myServers.every((s) => serverModes[s.id] !== 'mentions' && serverModes[s.id] !== 'none')
    && mutedList.length === 0;

  return `<section class="us-section">
    <h3 class="us-subsection-title">All Channels</h3>
    <div class="us-field">
      <div class="us-toggle-row">
        <span>
          <span class="us-toggle-label">${allChannelsOn ? 'Notifications are ON for every channel' : 'Notifications are limited on some channels'}</span>
          <span class="us-hint">One switch for every channel, category and role in all your servers — turns on desktop notifications, sets every server to "All Messages", and unmutes every channel.</span>
        </span>
        <button class="us-btn ${allChannelsOn ? 'us-btn-secondary' : 'us-btn-primary'}" id="us-n-all-on" type="button"${allChannelsOn ? ' disabled' : ''}>${allChannelsOn ? 'All on' : 'Turn all on'}</button>
      </div>
    </div>

    <h3 class="us-subsection-title">Desktop Notifications</h3>
    <div class="us-field">
      <label class="us-toggle-row">
        <span>
          <span class="us-toggle-label">Enable Desktop Notifications</span>
          <span class="us-hint" id="us-n-perm-hint">${esc(permHint)}</span>
        </span>
        <input type="checkbox" id="us-n-master" ${master ? 'checked' : ''}>
      </label>
    </div>
    <div class="us-field">
      <button class="us-btn us-btn-secondary" id="us-n-request" type="button" ${permStatus === 'granted' || permStatus === 'unsupported' ? 'disabled' : ''}>
        ${permStatus === 'granted' ? 'Permission Granted' : 'Enable'}
      </button>
    </div>

    <h3 class="us-subsection-title">Sounds</h3>
    <div class="us-field">
      <label class="us-toggle-row">
        <span>
          <span class="us-toggle-label">Notification Sound</span>
          <span class="us-hint">Plays a soft tone on new mentions and direct messages.</span>
        </span>
        <input type="checkbox" id="us-n-sound" ${sound ? 'checked' : ''}>
      </label>
    </div>

    <h3 class="us-subsection-title">Push Notifications</h3>
    <div class="us-field">
      <label class="us-toggle-row">
        <span>
          <span class="us-toggle-label">Enable Push Notifications</span>
          <span class="us-hint">Receive notifications even when the tab is closed. Uses self-hosted ntfy (no Google/Firebase).</span>
        </span>
        <input type="checkbox" id="us-n-ntfy" ${isNtfySubscribed() ? 'checked' : ''}>
      </label>
    </div>

    <h3 class="us-subsection-title">Server Notification Settings</h3>
    <div class="us-notif-servers">${serverRowsHtml}</div>

    <h3 class="us-subsection-title">Muted Channels</h3>
    <div class="us-notif-muted">${mutedHtml}</div>
  </section>`;
}

function wireNotifications(): void {
  if (!overlayEl) return;

  const master = overlayEl.querySelector<HTMLInputElement>('#us-n-master');
  master?.addEventListener('change', () => {
    try { localStorage.setItem('hub-people-notifs-master', String(master.checked)); } catch { /* ignore */ }
  });

  // GLOBAL "turn all on": master on + every server to All Messages + unmute all
  // channels + reset per-channel levels. One click = notifications for every
  // channel/category/role. Requests browser permission in the gesture if needed.
  const allOnBtn = overlayEl.querySelector<HTMLButtonElement>('#us-n-all-on');
  allOnBtn?.addEventListener('click', () => {
    if ('Notification' in window && Notification.permission === 'default') {
      requestNotificationPermissionSync(() => { /* stored by the browser */ });
    }
    try {
      localStorage.setItem('hub-people-notifs-master', 'true');  // master on
      localStorage.setItem('hub-people-server-notifs', '{}');     // every server → All Messages
      localStorage.setItem('hub-people-muted-channels', '[]');    // unmute every channel
      for (const k of Object.keys(localStorage)) {                // reset per-channel levels
        if (k.startsWith('hub-people-notif:')) localStorage.removeItem(k);
      }
    } catch { /* ignore */ }
    showToast('Notifications on for all channels');
    renderOverlay();
  });

  const sound = overlayEl.querySelector<HTMLInputElement>('#us-n-sound');
  sound?.addEventListener('change', () => {
    try { localStorage.setItem('hub-people-sound', String(sound.checked)); } catch { /* ignore */ }
  });

  // ntfy push toggle — explicit user action, never auto-subscribed.
  const ntfyToggle = overlayEl.querySelector<HTMLInputElement>('#us-n-ntfy');
  ntfyToggle?.addEventListener('change', () => {
    if (ntfyToggle.checked) {
      subscribeNtfy();
      showToast('Push notifications enabled');
    } else {
      unsubscribeNtfy();
      showToast('Push notifications disabled');
    }
  });

  const requestBtn = overlayEl.querySelector<HTMLButtonElement>('#us-n-request');
  requestBtn?.addEventListener('click', () => {
    if (!('Notification' in window)) return;
    // MUST be called synchronously from the click handler — no awaits
    // before this. Wrapping in async + await breaks the gesture on
    // Android Chrome and the prompt never appears.
    requestNotificationPermissionSync((p) => {
      const hint = overlayEl?.querySelector<HTMLElement>('#us-n-perm-hint');
      if (hint) {
        hint.textContent = p === 'granted'
          ? 'Your browser has granted permission.'
          : p === 'denied'
            ? 'Your browser has blocked notifications. Update site permissions to re-enable.'
            : "Click Enable to request your browser's permission.";
      }
      if (p === 'granted') {
        requestBtn.disabled = true;
        requestBtn.textContent = 'Permission Granted';
        showToast('Desktop notifications enabled');
      }
    });
  });

  overlayEl.querySelectorAll<HTMLSelectElement>('.us-notif-server-select').forEach((sel) => {
    sel.addEventListener('change', () => {
      const serverId = sel.getAttribute('data-server-id') || '';
      const mode = sel.value;
      if (!serverId) return;
      try {
        const raw = localStorage.getItem('hub-people-server-notifs');
        const obj = raw ? JSON.parse(raw) as Record<string, string> : {};
        if (mode === 'all') delete obj[serverId];
        else obj[serverId] = mode;
        localStorage.setItem('hub-people-server-notifs', JSON.stringify(obj));
      } catch { /* ignore */ }
    });
  });

  overlayEl.querySelectorAll<HTMLButtonElement>('.us-notif-unmute').forEach((btn) => {
    btn.addEventListener('click', () => {
      const ch = btn.getAttribute('data-channel') || '';
      if (!ch) return;
      try {
        const raw = localStorage.getItem('hub-people-muted-channels');
        const arr = raw ? JSON.parse(raw) as unknown : [];
        if (Array.isArray(arr)) {
          const next = arr.filter((x) => typeof x === 'string' && x !== ch);
          localStorage.setItem('hub-people-muted-channels', JSON.stringify(next));
        }
      } catch { /* ignore */ }
      btn.closest('.us-notif-muted-row')?.remove();
      const list = overlayEl?.querySelector('.us-notif-muted');
      if (list && list.querySelectorAll('.us-notif-muted-row').length === 0) {
        list.innerHTML = `<div class="us-hint">No channels muted.</div>`;
      }
    });
  });
}

// ─── Voice & Video (Discord 1:1) ───
function renderVoice(): string {
  const savedMic = localStorage.getItem('hub-people-mic-device') || '';
  const savedSpeaker = localStorage.getItem('hub-people-speaker-device') || '';
  const savedCam = localStorage.getItem('hub-people-cam-device') || '';
  const ptt = localStorage.getItem('hub-people-ptt-key') || '';
  const inputVol = Number(localStorage.getItem('hub-people-input-volume') ?? '100');
  const outputVol = Number(localStorage.getItem('hub-people-output-volume') ?? '100');
  const sensMode = localStorage.getItem('hub-people-sensitivity-mode') || 'auto';
  const sensThreshold = Number(localStorage.getItem('hub-people-sensitivity-threshold') ?? '-50');
  const mode = localStorage.getItem('hub-people-voice-mode') || 'vad';
  const resolution = localStorage.getItem('hub-people-cam-resolution') || '720p';
  const fps = localStorage.getItem('hub-people-cam-fps') || '30';

  return `<section class="us-section">
    <div id="us-voice-consent" class="us-consent-banner" style="display:none">
      <p class="us-hint">Your browser hasn't shared microphone access yet, so device names aren't visible.</p>
      <button class="us-btn us-btn-primary" id="us-voice-grant" type="button">Grant microphone access</button>
    </div>

    <h3 class="us-subsection-title">Voice Settings</h3>
    <div class="us-field-split">
      <div class="us-field">
        <label class="us-label" for="us-v-mic">Input Device</label>
        <select class="us-input" id="us-v-mic" data-saved="${escAttr(savedMic)}"><option value="">Default</option></select>
      </div>
      <div class="us-field">
        <label class="us-label" for="us-v-speaker">Output Device</label>
        <select class="us-input" id="us-v-speaker" data-saved="${escAttr(savedSpeaker)}"><option value="">Default</option></select>
      </div>
    </div>

    <div class="us-field">
      <label class="us-label" for="us-v-input-vol">Input Volume <span class="us-slider-value" id="us-v-input-vol-val">${inputVol}%</span></label>
      <div class="us-slider-row">
        <input type="range" class="us-slider" id="us-v-input-vol" min="0" max="200" step="1" value="${inputVol}">
      </div>
    </div>
    <div class="us-field">
      <label class="us-label" for="us-v-output-vol">Output Volume <span class="us-slider-value" id="us-v-output-vol-val">${outputVol}%</span></label>
      <div class="us-slider-row">
        <input type="range" class="us-slider" id="us-v-output-vol" min="0" max="200" step="1" value="${outputVol}">
      </div>
    </div>

    <h3 class="us-subsection-title">Mic Test</h3>
    <div class="us-field">
      <div class="us-hint">Click below and speak — the green bar will move to confirm your microphone is working.</div>
      <div class="us-mic-test">
        <button class="us-btn us-btn-secondary" id="us-v-mic-test" type="button">Let's Check</button>
        <div class="us-mic-meter" aria-hidden="true"><div class="us-mic-meter-fill" id="us-v-mic-meter"></div></div>
      </div>
    </div>

    <h3 class="us-subsection-title">Input Sensitivity</h3>
    <div class="us-field">
      <div class="us-radio-row">
        <label><input type="radio" name="us-v-sens" value="auto" ${sensMode === 'auto' ? 'checked' : ''}> Automatically determine input sensitivity</label>
        <label><input type="radio" name="us-v-sens" value="manual" ${sensMode === 'manual' ? 'checked' : ''}> Manually set input sensitivity</label>
      </div>
    </div>
    <div class="us-field" id="us-v-sens-manual" style="${sensMode === 'manual' ? '' : 'display:none'}">
      <label class="us-label" for="us-v-sens-threshold">Threshold <span class="us-slider-value" id="us-v-sens-threshold-val">${sensThreshold} dB</span></label>
      <input type="range" class="us-slider" id="us-v-sens-threshold" min="-100" max="0" step="1" value="${sensThreshold}">
    </div>

    <h3 class="us-subsection-title">Input Mode</h3>
    <div class="us-field">
      <div class="us-radio-row">
        <label><input type="radio" name="us-v-mode" value="vad" ${mode === 'vad' ? 'checked' : ''}> Voice Activity</label>
        <label><input type="radio" name="us-v-mode" value="ptt" ${mode === 'ptt' ? 'checked' : ''}> Push to Talk</label>
      </div>
    </div>
    <div class="us-field" id="us-v-ptt-wrap" style="${mode === 'ptt' ? '' : 'display:none'}">
      <label class="us-label" for="us-v-ptt">Push-to-Talk Key</label>
      <input class="us-input us-input-mono" id="us-v-ptt" value="${escAttr(ptt)}" placeholder="Click then press a key..." maxlength="32" readonly>
    </div>

    <h3 class="us-subsection-title">Video Settings</h3>
    <div class="us-field">
      <label class="us-label" for="us-v-cam">Camera</label>
      <select class="us-input" id="us-v-cam" data-saved="${escAttr(savedCam)}"><option value="">Default</option></select>
    </div>
    <div class="us-field">
      <div class="us-cam-preview" id="us-v-cam-preview-wrap">
        <video id="us-v-cam-preview" class="us-cam-preview-video" autoplay muted playsinline></video>
        <div class="us-cam-preview-placeholder" id="us-v-cam-placeholder">
          <span class="us-hint">Click Test Video to preview your camera</span>
        </div>
      </div>
      <div style="display:flex;gap:8px;margin-top:8px">
        <button class="us-btn us-btn-secondary us-btn-sm" id="us-v-cam-test" type="button">Test Video</button>
        <button class="us-btn us-btn-secondary us-btn-sm" id="us-v-cam-stop" type="button">Stop</button>
      </div>
    </div>
    <div class="us-field-split">
      <div class="us-field">
        <label class="us-label" for="us-v-resolution">Resolution</label>
        <select class="us-input" id="us-v-resolution">
          ${['480p','720p','1080p'].map(r => `<option value="${r}" ${resolution === r ? 'selected' : ''}>${r}</option>`).join('')}
        </select>
      </div>
      <div class="us-field">
        <label class="us-label" for="us-v-fps">Frame Rate</label>
        <select class="us-input" id="us-v-fps">
          ${['15','30','60'].map(f => `<option value="${f}" ${fps === f ? 'selected' : ''}>${f} fps</option>`).join('')}
        </select>
      </div>
    </div>

    <div class="us-row-actions">
      <button class="us-btn us-btn-primary" id="us-v-save" type="button">Save</button>
    </div>
  </section>`;
}

async function populateMediaDevices(): Promise<void> {
  const micSel = document.getElementById('us-v-mic') as HTMLSelectElement | null;
  const spkSel = document.getElementById('us-v-speaker') as HTMLSelectElement | null;
  const camSel = document.getElementById('us-v-cam') as HTMLSelectElement | null;
  const consent = document.getElementById('us-voice-consent');
  if (!micSel || !spkSel) return;

  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const hasLabels = devices.some(d => d.label);
    if (!hasLabels) { if (consent) consent.style.display = 'block'; return; }
    if (consent) consent.style.display = 'none';

    const inputs = devices.filter(d => d.kind === 'audioinput');
    const outputs = devices.filter(d => d.kind === 'audiooutput');
    const cameras = devices.filter(d => d.kind === 'videoinput');
    const savedMic = micSel.dataset['saved'] || '';
    const savedSpk = spkSel.dataset['saved'] || '';
    const savedCam = camSel?.dataset['saved'] || '';
    micSel.innerHTML = '<option value="">Default</option>' + inputs.map(d =>
      `<option value="${escAttr(d.deviceId)}" ${d.deviceId === savedMic ? 'selected' : ''}>${esc(d.label || d.deviceId.slice(0, 8))}</option>`
    ).join('');
    spkSel.innerHTML = '<option value="">Default</option>' + outputs.map(d =>
      `<option value="${escAttr(d.deviceId)}" ${d.deviceId === savedSpk ? 'selected' : ''}>${esc(d.label || d.deviceId.slice(0, 8))}</option>`
    ).join('');
    if (camSel) {
      camSel.innerHTML = '<option value="">Default</option>' + cameras.map(d =>
        `<option value="${escAttr(d.deviceId)}" ${d.deviceId === savedCam ? 'selected' : ''}>${esc(d.label || d.deviceId.slice(0, 8))}</option>`
      ).join('');
    }
  } catch {
    if (consent) consent.style.display = 'block';
  }
}

let micTestStream: MediaStream | null = null;
let micTestCtx: AudioContext | null = null;
let micTestRaf = 0;

function stopMicTest(): void {
  if (micTestRaf) { cancelAnimationFrame(micTestRaf); micTestRaf = 0; }
  if (micTestStream) { micTestStream.getTracks().forEach(t => t.stop()); micTestStream = null; }
  if (micTestCtx) { void micTestCtx.close(); micTestCtx = null; }
  const fill = document.getElementById('us-v-mic-meter');
  if (fill) fill.style.width = '0%';
}

async function startMicTest(): Promise<void> {
  stopMicTest();
  try {
    const deviceId = (document.getElementById('us-v-mic') as HTMLSelectElement | null)?.value || '';
    const constraints: MediaStreamConstraints = { audio: deviceId ? { deviceId: { exact: deviceId } } : true };
    micTestStream = await navigator.mediaDevices.getUserMedia(constraints);
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    micTestCtx = new Ctx();
    const src = micTestCtx.createMediaStreamSource(micTestStream);
    const analyser = micTestCtx.createAnalyser();
    analyser.fftSize = 1024;
    src.connect(analyser);
    const buf = new Uint8Array(analyser.frequencyBinCount);
    const fill = document.getElementById('us-v-mic-meter');
    const tick = (): void => {
      analyser.getByteTimeDomainData(buf);
      let peak = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = Math.abs((buf[i] ?? 128) - 128);
        if (v > peak) peak = v;
      }
      const pct = Math.min(100, Math.round((peak / 128) * 160));
      if (fill) fill.style.width = `${pct}%`;
      micTestRaf = requestAnimationFrame(tick);
    };
    tick();
  } catch (e) {
    showToast('Mic test failed: ' + String(e), 'err');
    stopMicTest();
  }
}

let camTestStream: MediaStream | null = null;

function stopCamTest(): void {
  if (camTestStream) { camTestStream.getTracks().forEach(t => t.stop()); camTestStream = null; }
  const video = document.getElementById('us-v-cam-preview') as HTMLVideoElement | null;
  const ph = document.getElementById('us-v-cam-placeholder');
  if (video) { video.srcObject = null; video.style.display = 'none'; }
  if (ph) ph.style.display = '';
}

async function startCamTest(): Promise<void> {
  stopCamTest();
  try {
    const deviceId = (document.getElementById('us-v-cam') as HTMLSelectElement | null)?.value || '';
    const res = (document.getElementById('us-v-resolution') as HTMLSelectElement | null)?.value || '720p';
    const height = res === '1080p' ? 1080 : res === '480p' ? 480 : 720;
    const fps = Number((document.getElementById('us-v-fps') as HTMLSelectElement | null)?.value || '30');
    const constraints: MediaStreamConstraints = {
      video: {
        ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
        height: { ideal: height },
        frameRate: { ideal: fps },
      },
    };
    camTestStream = await navigator.mediaDevices.getUserMedia(constraints);
    const video = document.getElementById('us-v-cam-preview') as HTMLVideoElement | null;
    const ph = document.getElementById('us-v-cam-placeholder');
    if (video) { video.srcObject = camTestStream; video.style.display = 'block'; }
    if (ph) ph.style.display = 'none';
  } catch (e) {
    showToast('Camera test failed: ' + String(e), 'err');
    stopCamTest();
  }
}

function wireVoice(): void {
  if (!overlayEl) return;
  void populateMediaDevices();

  document.getElementById('us-voice-grant')?.addEventListener('click', async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());
      await populateMediaDevices();
      showToast('Microphone access granted', 'ok');
    } catch (e) {
      showToast('Mic access denied: ' + String(e), 'err');
    }
  });

  const pttInput = document.getElementById('us-v-ptt') as HTMLInputElement | null;
  pttInput?.addEventListener('click', () => {
    pttInput.value = '';
    pttInput.placeholder = 'Press any key...';
    const capture = (e: KeyboardEvent): void => {
      e.preventDefault();
      pttInput.value = e.key.length === 1 ? e.key.toUpperCase() : e.key;
      document.removeEventListener('keydown', capture, true);
    };
    document.addEventListener('keydown', capture, true);
  });

  // Sliders — live % + persist + apply to any active voice session.
  const bindSlider = (id: string, valId: string, key: string, suffix: string): void => {
    const el = document.getElementById(id) as HTMLInputElement | null;
    const label = document.getElementById(valId);
    if (!el || !label) return;
    el.addEventListener('input', () => {
      label.textContent = `${el.value}${suffix}`;
      localStorage.setItem(key, el.value);
    });
  };
  bindSlider('us-v-input-vol', 'us-v-input-vol-val', 'hub-people-input-volume', '%');
  bindSlider('us-v-output-vol', 'us-v-output-vol-val', 'hub-people-output-volume', '%');
  bindSlider('us-v-sens-threshold', 'us-v-sens-threshold-val', 'hub-people-sensitivity-threshold', ' dB');

  // Sensitivity mode radios toggle the manual threshold field.
  overlayEl.querySelectorAll<HTMLInputElement>('input[name="us-v-sens"]').forEach(r => {
    r.addEventListener('change', () => {
      localStorage.setItem('hub-people-sensitivity-mode', r.value);
      const wrap = document.getElementById('us-v-sens-manual');
      if (wrap) wrap.style.display = r.value === 'manual' ? '' : 'none';
    });
  });

  // Input mode radios toggle the PTT keybind field.
  overlayEl.querySelectorAll<HTMLInputElement>('input[name="us-v-mode"]').forEach(r => {
    r.addEventListener('change', () => {
      localStorage.setItem('hub-people-voice-mode', r.value);
      const wrap = document.getElementById('us-v-ptt-wrap');
      if (wrap) wrap.style.display = r.value === 'ptt' ? '' : 'none';
    });
  });

  // Mic Test — toggle.
  const micTestBtn = document.getElementById('us-v-mic-test') as HTMLButtonElement | null;
  micTestBtn?.addEventListener('click', () => {
    if (micTestStream) {
      stopMicTest();
      micTestBtn.textContent = "Let's Check";
    } else {
      void startMicTest();
      micTestBtn.textContent = 'Stop';
    }
  });

  document.getElementById('us-v-cam-test')?.addEventListener('click', () => { void startCamTest(); });
  document.getElementById('us-v-cam-stop')?.addEventListener('click', () => { stopCamTest(); });

  // Device dropdowns — persist + (if voice session is live) apply to LiveKit.
  const micSel = document.getElementById('us-v-mic') as HTMLSelectElement | null;
  micSel?.addEventListener('change', () => {
    localStorage.setItem('hub-people-mic-device', micSel.value);
    void import('../channels/voice').then(({ applyInputDevice }) => { void applyInputDevice(micSel.value); });
  });
  const spkSel = document.getElementById('us-v-speaker') as HTMLSelectElement | null;
  spkSel?.addEventListener('change', () => {
    localStorage.setItem('hub-people-speaker-device', spkSel.value);
    void import('../channels/voice').then(({ applyOutputDevice }) => { void applyOutputDevice(spkSel.value); });
  });
  const camSel = document.getElementById('us-v-cam') as HTMLSelectElement | null;
  camSel?.addEventListener('change', () => {
    localStorage.setItem('hub-people-cam-device', camSel.value);
  });
  const resSel = document.getElementById('us-v-resolution') as HTMLSelectElement | null;
  resSel?.addEventListener('change', () => localStorage.setItem('hub-people-cam-resolution', resSel.value));
  const fpsSel = document.getElementById('us-v-fps') as HTMLSelectElement | null;
  fpsSel?.addEventListener('change', () => localStorage.setItem('hub-people-cam-fps', fpsSel.value));

  document.getElementById('us-v-save')?.addEventListener('click', () => {
    const mic = micSel?.value || '';
    const spk = spkSel?.value || '';
    const cam = camSel?.value || '';
    const ptt = (document.getElementById('us-v-ptt') as HTMLInputElement | null)?.value || '';
    const btn = document.getElementById('us-v-save') as HTMLButtonElement | null;
    if (btn) btn.classList.add('us-btn-saving');
    localStorage.setItem('hub-people-mic-device', mic);
    localStorage.setItem('hub-people-speaker-device', spk);
    localStorage.setItem('hub-people-cam-device', cam);
    localStorage.setItem('hub-people-ptt-key', ptt);
    setTimeout(() => {
      if (btn) btn.classList.remove('us-btn-saving');
      showToast('Voice settings saved', 'ok');
    }, 200);
  });
}

// ─── Keybinds (read-only v1) ───
function renderKeybinds(): string {
  const binds: Array<[string, string]> = [
    ['Open Settings', 'Ctrl / ⌘ + ,'],
    ['Focus Search', 'Ctrl / ⌘ + K'],
    ['Toggle Members', 'Ctrl / ⌘ + Shift + M'],
    ['Next Channel', 'Alt + ↓'],
    ['Previous Channel', 'Alt + ↑'],
    ['Next Server', 'Alt + Shift + ↓'],
    ['Previous Server', 'Alt + Shift + ↑'],
    ['Reply to hovered message', 'R'],
    ['Send message', 'Enter'],
    ['Newline in compose', 'Shift + Enter'],
    ['Bold', 'Ctrl / ⌘ + B'],
    ['Italic', 'Ctrl / ⌘ + I'],
    ['Cancel reply / close popovers', 'Esc'],
  ];
  const rows = binds.map(([label, combo]) =>
    `<div class="us-kb-row"><span class="us-kb-label">${esc(label)}</span><kbd class="us-kb-combo">${esc(combo)}</kbd></div>`
  ).join('');
  return `<section class="us-section">
    <div class="us-placeholder">
      <p class="us-hint">Remapping is coming later — this list reflects what's currently wired.</p>
      <div class="us-kb-list">${rows}</div>
    </div>
  </section>`;
}

// ─── Feature Flags (admin only) ───

/** Cached flags for the settings overlay lifetime. */
let flagsCache: FeatureFlag[] = [];
let flagsLoaded = false;

function isSettingsAdmin(): boolean {
  return !!state.ownerId && state.servers.some(s => s.owner_id === state.ownerId);
}

function renderFlags(): string {
  if (!flagsLoaded) {
    // Trigger async load; re-render when done
    void loadFlags();
    return `<section class="us-section"><div class="us-hint">Loading flags...</div></section>`;
  }
  const rows = flagsCache.map((f, i) => `
    <tr class="us-ff-row" data-idx="${i}">
      <td class="us-ff-key">${esc(f.key)}</td>
      <td>
        <label class="us-toggle">
          <input type="checkbox" class="us-ff-enabled" data-key="${escAttr(f.key)}" ${f.enabled ? 'checked' : ''}>
          <span class="us-toggle-slider"></span>
        </label>
      </td>
      <td>
        <select class="us-input us-ff-stage" data-key="${escAttr(f.key)}">
          ${['dev','alpha','beta','prod'].map(s => `<option value="${s}" ${f.stage === s ? 'selected' : ''}>${s}</option>`).join('')}
        </select>
      </td>
      <td><input class="us-input us-ff-desc" data-key="${escAttr(f.key)}" value="${escAttr(f.description)}" placeholder="Description"></td>
      <td>
        <div class="us-ff-chips" data-key="${escAttr(f.key)}">
          ${f.allowlist.map(id => `<span class="us-ff-chip">${esc(id.slice(0, 8))}<button class="us-ff-chip-x" data-key="${escAttr(f.key)}" data-id="${escAttr(id)}" type="button" title="Remove">&times;</button></span>`).join('')}
          <input class="us-ff-chip-input" data-key="${escAttr(f.key)}" placeholder="+ person ID" maxlength="64">
        </div>
      </td>
      <td class="us-ff-actions">
        <button class="us-btn us-btn-primary us-btn-sm us-ff-save" data-key="${escAttr(f.key)}" type="button">Save</button>
        <button class="us-btn us-btn-danger us-btn-sm us-ff-delete" data-key="${escAttr(f.key)}" type="button">Delete</button>
      </td>
    </tr>`).join('');

  return `<section class="us-section">
    <div class="us-row-actions" style="margin-bottom:12px">
      <button class="us-btn us-btn-primary" id="us-ff-new" type="button">+ New flag</button>
    </div>
    <div class="us-ff-table-wrap">
      <table class="us-ff-table">
        <thead><tr>
          <th>Key</th><th>On</th><th>Stage</th><th>Description</th><th>Allowlist</th><th></th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
    ${flagsCache.length === 0 ? '<div class="us-hint" style="margin-top:12px">No feature flags yet.</div>' : ''}
  </section>`;
}

async function loadFlags(): Promise<void> {
  try {
    const pid = state.ownerId || '';
    flagsCache = await get<FeatureFlag[]>(`/api/feature-flags/all?person_id=${encodeURIComponent(pid)}`);
    flagsLoaded = true;
    renderOverlay();
  } catch (e) {
    showToast('Failed to load flags: ' + String(e), 'err');
  }
}

function wireFlags(): void {
  // Optimistic toggle
  overlayEl?.querySelectorAll<HTMLInputElement>('.us-ff-enabled').forEach(el => {
    el.addEventListener('change', () => {
      const key = el.dataset['key'] || '';
      const flag = flagsCache.find(f => f.key === key);
      if (!flag) return;
      const prev = flag.enabled;
      flag.enabled = el.checked;
      void post<unknown>('/api/feature-flags/upsert', {
        key, person_id: state.ownerId || '', enabled: el.checked,
      }).then(() => {
        showToast(`${key} ${el.checked ? 'enabled' : 'disabled'}`, 'ok');
      }).catch(e => {
        flag.enabled = prev;
        el.checked = prev;
        showToast(String(e), 'err');
      });
    });
  });

  // Save button per row
  overlayEl?.querySelectorAll<HTMLButtonElement>('.us-ff-save').forEach(btn => {
    btn.addEventListener('click', () => {
      const key = btn.dataset['key'] || '';
      const flag = flagsCache.find(f => f.key === key);
      if (!flag) return;
      const stageEl = overlayEl?.querySelector<HTMLSelectElement>(`.us-ff-stage[data-key="${CSS.escape(key)}"]`);
      const descEl = overlayEl?.querySelector<HTMLInputElement>(`.us-ff-desc[data-key="${CSS.escape(key)}"]`);
      const stage = stageEl?.value || flag.stage;
      const description = descEl?.value ?? flag.description;
      void post<unknown>('/api/feature-flags/upsert', {
        key, person_id: state.ownerId || '', stage, description, allowlist: flag.allowlist,
      }).then(() => {
        flag.stage = stage as FeatureFlag['stage'];
        flag.description = description;
        showToast(`${key} saved`, 'ok');
      }).catch(e => showToast(String(e), 'err'));
    });
  });

  // Delete button per row
  overlayEl?.querySelectorAll<HTMLButtonElement>('.us-ff-delete').forEach(btn => {
    btn.addEventListener('click', () => {
      const key = btn.dataset['key'] || '';
      confirmModal('Delete flag', `Delete flag "${key}"?`, () => {
        void post<unknown>('/api/feature-flags/delete', {
          key, person_id: state.ownerId || '',
        }).then(() => {
          flagsCache = flagsCache.filter(f => f.key !== key);
          showToast(`${key} deleted`, 'ok');
          renderOverlay();
        }).catch(e => showToast(String(e), 'err'));
      }, { confirmLabel: 'Delete', variant: 'danger' });
    });
  });

  // Allowlist chip remove
  overlayEl?.querySelectorAll<HTMLButtonElement>('.us-ff-chip-x').forEach(btn => {
    btn.addEventListener('click', () => {
      const key = btn.dataset['key'] || '';
      const id = btn.dataset['id'] || '';
      const flag = flagsCache.find(f => f.key === key);
      if (!flag) return;
      flag.allowlist = flag.allowlist.filter(a => a !== id);
      renderOverlay();
    });
  });

  // Allowlist chip add (Enter in input)
  overlayEl?.querySelectorAll<HTMLInputElement>('.us-ff-chip-input').forEach(input => {
    input.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      const key = input.dataset['key'] || '';
      const val = input.value.trim();
      if (!val) return;
      const flag = flagsCache.find(f => f.key === key);
      if (!flag) return;
      if (flag.allowlist.includes(val)) { showToast('Already in allowlist', 'warn'); return; }
      flag.allowlist.push(val);
      renderOverlay();
    });
  });

  // New flag button
  document.getElementById('us-ff-new')?.addEventListener('click', () => {
    promptModal(
      'New feature flag',
      'Flag key (e.g. my_new_feature):',
      (key) => {
        if (!key.trim()) return;
        const trimmed = key.trim();
        if (flagsCache.some(f => f.key === trimmed)) { showToast('Flag already exists', 'warn'); return; }
        void post<unknown>('/api/feature-flags/upsert', {
          key: trimmed, person_id: state.ownerId || '', enabled: false, stage: 'dev', description: '',
        }).then(() => {
          flagsCache.push({ key: trimmed, enabled: false, stage: 'dev', allowlist: [], description: '' });
          showToast(`${trimmed} created`, 'ok');
          renderOverlay();
        }).catch(e => showToast(String(e), 'err'));
      },
      { placeholder: 'my_new_feature', confirmLabel: 'Create', required: true }
    );
  });
}

// ─── What's New ───
function renderWhatsNew(): string {
  const entries: Array<[string, string]> = [
    ['Batch 7.5', 'Discord-style reply UX, full User Settings overlay, channel-list visibility audit.'],
    ['Batch 7', 'Unified right-click menus, voice grid rebuild, video + screen share.'],
    ['Batch 6', 'Warm 2-tone theme, account widget, CSS architecture.'],
    ['Batch 5', 'Pinned messages, markdown, emoji reactions, thread panel polish.'],
  ];
  const rows = entries.map(([title, body]) =>
    `<div class="us-whatsnew-row"><div class="us-whatsnew-title">${esc(title)}</div><div class="us-whatsnew-body">${esc(body)}</div></div>`
  ).join('');
  return `<section class="us-section">
    <div class="us-placeholder">${rows}</div>
  </section>`;
}

// ─── Bookings (worker dashboard — batch 5d) ───
//
// Five cards, matching the Calendly "Manage" surface:
//   1. Public link (copy + edit slug)
//   2. Session details (minutes, price, currency, blurb, cancel window)
//   3. Weekly availability grid (7 rows × start/end-minute time ranges)
//   4. Blackouts (date ranges to block off)
//   5. Upcoming + past bookings (confirm / cancel / complete / mark-paid)

interface BookingMe {
  person_id: string;
  display_name: string;
  slug: string | null;
  session_minutes: number;
  price_cents: number;
  currency: string;
  blurb: string;
  cancel_hours: number;
  public_url: string | null;
  square_configured: boolean;
}

interface AvailabilityRow {
  id: string;
  person_id: string;
  day_of_week: number;
  start_minute: number;
  end_minute: number;
  timezone: string;
  created_at: string;
}

interface BlackoutRow {
  id: string;
  person_id: string;
  start_at: string;
  end_at: string;
  reason: string;
  created_at: string;
}

interface BookingRow {
  id: string;
  worker_id: string;
  client_name: string;
  client_email: string;
  client_phone: string;
  client_notes: string;
  scheduled_start: string;
  scheduled_end: string;
  timezone: string;
  status: string;
  payment_status: string;
  amount_cents: number;
  currency: string;
  confirmation_token: string;
  cancel_token: string;
  created_at: string;
  updated_at: string;
}

let bookingsCache: {
  me: BookingMe | null;
  availability: AvailabilityRow[];
  blackouts: BlackoutRow[];
  bookings: BookingRow[];
  loaded: boolean;
} = { me: null, availability: [], blackouts: [], bookings: [], loaded: false };

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function minutesToHhmm(m: number): string {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

function hhmmToMinutes(s: string): number | null {
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const mm = Number(m[2]);
  if (!(h >= 0 && h <= 24) || !(mm >= 0 && mm < 60)) return null;
  return h * 60 + mm;
}

function formatBookingPrice(cents: number, currency: string): string {
  if (cents <= 0) return 'Free';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

function renderBookings(): string {
  if (!bookingsCache.loaded) {
    void loadBookings();
    return `<section class="us-section"><div class="us-hint">Loading bookings...</div></section>`;
  }
  const me = bookingsCache.me;
  if (!me) {
    return `<section class="us-section"><div class="us-hint">Could not load your booking settings.</div></section>`;
  }

  const publicLinkCard = `
    <div class="us-ff-table-wrap" style="margin-bottom:20px">
      <h3 style="margin:0 0 12px;font-size:15px">Public booking link</h3>
      <div class="us-field">
        <label class="us-label" for="us-bk-slug">URL handle</label>
        <div style="display:flex;gap:8px;align-items:center">
          <span class="us-hint us-hint-inline" style="white-space:nowrap;font-family:monospace">/book/</span>
          <input class="us-input us-input-mono" id="us-bk-slug" value="${escAttr(me.slug || '')}" placeholder="your-name" maxlength="48" autocomplete="off" style="flex:1">
          <button class="us-btn us-btn-primary" id="us-bk-slug-save" type="button">Save</button>
        </div>
        <div class="us-hint">Lowercase a–z, 0–9, dashes. Leave empty to disable your public page.</div>
      </div>
      ${me.public_url ? `
        <div class="us-field">
          <label class="us-label">Shareable link</label>
          <div style="display:flex;gap:8px;align-items:center">
            <input class="us-input us-input-mono" id="us-bk-link" readonly value="${escAttr(me.public_url)}" style="flex:1">
            <button class="us-btn us-btn-secondary" id="us-bk-copy" type="button">${iconCopy()} Copy</button>
          </div>
        </div>` : ''}
    </div>`;

  const sessionCard = `
    <div class="us-ff-table-wrap" style="margin-bottom:20px">
      <h3 style="margin:0 0 12px;font-size:15px">Session details</h3>
      <div class="us-field"><label class="us-label" for="us-bk-mins">Duration (minutes)</label>
        <input class="us-input" id="us-bk-mins" type="number" min="5" max="480" value="${me.session_minutes}"></div>
      <div class="us-field"><label class="us-label" for="us-bk-price">Price (cents)</label>
        <input class="us-input" id="us-bk-price" type="number" min="0" max="1000000" value="${me.price_cents}">
        <div class="us-hint">Current: ${esc(formatBookingPrice(me.price_cents, me.currency))}. Set 0 for a free session.</div>
      </div>
      <div class="us-field"><label class="us-label" for="us-bk-currency">Currency</label>
        <select class="us-input" id="us-bk-currency">
          ${['AUD', 'USD', 'EUR', 'GBP', 'NZD', 'CAD'].map(c => `<option value="${c}" ${me.currency === c ? 'selected' : ''}>${c}</option>`).join('')}
        </select>
      </div>
      <div class="us-field"><label class="us-label" for="us-bk-cancel">Cancellation window (hours before)</label>
        <input class="us-input" id="us-bk-cancel" type="number" min="0" max="720" value="${me.cancel_hours}">
        <div class="us-hint">Clients can cancel up to this many hours before the start time.</div>
      </div>
      <div class="us-field"><label class="us-label" for="us-bk-blurb">Public blurb</label>
        <textarea class="us-input" id="us-bk-blurb" rows="3" maxlength="600" placeholder="A short description shown on your booking page.">${esc(me.blurb)}</textarea>
      </div>
      <div class="us-row-actions">
        <button class="us-btn us-btn-primary" id="us-bk-session-save" type="button">Save session details</button>
      </div>
      ${!me.square_configured ? `<div class="us-hint" style="margin-top:12px;color:var(--warning,#ea580c)">Online payments are not configured on this server. Bookings currently fall back to "pay on arrival" — you can mark each one paid manually in the list below.</div>` : ''}
    </div>`;

  const availabilityCard = `
    <div class="us-ff-table-wrap" style="margin-bottom:20px">
      <h3 style="margin:0 0 12px;font-size:15px">Weekly availability</h3>
      <div class="us-hint" style="margin-bottom:12px">Define one or more time windows per day. Times are in 24h local (HH:MM).</div>
      <div id="us-bk-avail-list"></div>
      <div class="us-row-actions" style="margin-top:12px">
        <button class="us-btn us-btn-secondary" id="us-bk-avail-add" type="button">+ Add window</button>
        <button class="us-btn us-btn-primary" id="us-bk-avail-save" type="button">Save availability</button>
      </div>
    </div>`;

  const blackoutsRows = bookingsCache.blackouts.length === 0
    ? '<div class="us-hint">No blackouts yet. Add a date range below to block time off.</div>'
    : bookingsCache.blackouts.map(b => `
        <div class="us-conn-row" data-blackout-id="${escAttr(b.id)}">
          <div class="us-conn-value">${esc(b.start_at.replace('T', ' '))} → ${esc(b.end_at.replace('T', ' '))}${b.reason ? ` — <span class="us-hint-inline">${esc(b.reason)}</span>` : ''}</div>
          <button class="us-btn us-btn-danger us-btn-sm" data-blackout-del="${escAttr(b.id)}" type="button">${iconTrash()}</button>
        </div>`).join('');

  const blackoutsCard = `
    <div class="us-ff-table-wrap" style="margin-bottom:20px">
      <h3 style="margin:0 0 12px;font-size:15px">Blackouts</h3>
      <div>${blackoutsRows}</div>
      <details style="margin-top:12px">
        <summary style="cursor:pointer;font-size:13px">+ Add blackout</summary>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:8px">
          <div><label class="us-label">Start (YYYY-MM-DDTHH:MM:SS)</label>
            <input class="us-input us-input-mono" id="us-bk-bl-start" placeholder="2026-05-01T09:00:00"></div>
          <div><label class="us-label">End</label>
            <input class="us-input us-input-mono" id="us-bk-bl-end" placeholder="2026-05-01T17:00:00"></div>
          <div style="grid-column: 1 / -1"><label class="us-label">Reason (optional)</label>
            <input class="us-input" id="us-bk-bl-reason" maxlength="200"></div>
        </div>
        <div class="us-row-actions" style="margin-top:8px">
          <button class="us-btn us-btn-primary" id="us-bk-bl-add" type="button">Add blackout</button>
        </div>
      </details>
    </div>`;

  const bookingRows = bookingsCache.bookings.length === 0
    ? '<div class="us-hint">No bookings yet. Share your public link to get started.</div>'
    : bookingsCache.bookings.map(b => {
        const when = `${b.scheduled_start.replace('T', ' ')}`;
        const payBadge = b.payment_status === 'paid' ? '<span class="us-ff-chip" style="background:var(--accent-soft,rgba(22,163,74,0.15));color:var(--accent,#16a34a)">paid</span>'
          : '<span class="us-ff-chip" style="background:var(--warning-soft,rgba(234,88,12,0.15));color:var(--warning,#ea580c)">unpaid</span>';
        const statusBadge = `<span class="us-ff-chip">${esc(b.status)}</span>`;
        return `<div class="us-conn-row" data-booking-id="${escAttr(b.id)}" style="align-items:flex-start;flex-wrap:wrap;gap:6px">
          <div class="us-conn-value" style="flex:1;min-width:240px">
            <div><strong>${esc(b.client_name)}</strong> · ${esc(b.client_email)}</div>
            <div class="us-hint-inline">${esc(when)} (${esc(b.timezone)})</div>
            ${b.client_notes ? `<div class="us-hint-inline" style="margin-top:2px">“${esc(b.client_notes)}”</div>` : ''}
          </div>
          <div style="display:flex;gap:4px;align-items:center;flex-wrap:wrap">
            ${statusBadge}${payBadge}
            ${b.status === 'pending' ? `<button class="us-btn us-btn-primary us-btn-sm" data-bk-action="confirm" data-bk-id="${escAttr(b.id)}" type="button">Confirm</button>` : ''}
            ${b.status !== 'cancelled' && b.status !== 'completed' ? `<button class="us-btn us-btn-danger us-btn-sm" data-bk-action="cancel" data-bk-id="${escAttr(b.id)}" type="button">Cancel</button>` : ''}
            ${b.status === 'confirmed' ? `<button class="us-btn us-btn-secondary us-btn-sm" data-bk-action="complete" data-bk-id="${escAttr(b.id)}" type="button">Complete</button>` : ''}
            ${b.payment_status !== 'paid' && b.amount_cents > 0 ? `<button class="us-btn us-btn-secondary us-btn-sm" data-bk-action="mark-paid" data-bk-id="${escAttr(b.id)}" type="button">Mark paid</button>` : ''}
          </div>
        </div>`;
      }).join('');

  const bookingsCard = `
    <div class="us-ff-table-wrap">
      <h3 style="margin:0 0 12px;font-size:15px">Bookings</h3>
      <div>${bookingRows}</div>
    </div>`;

  return `<section class="us-section">
    ${publicLinkCard}${sessionCard}${availabilityCard}${blackoutsCard}${bookingsCard}
  </section>`;
}

async function loadBookings(): Promise<void> {
  const pid = state.ownerId || '';
  if (!pid) { bookingsCache.loaded = true; renderOverlay(); return; }
  try {
    const [me, avail, bls, bks] = await Promise.all([
      get<BookingMe>(`/api/booking/me?person_id=${encodeURIComponent(pid)}`),
      get<AvailabilityRow[]>(`/api/booking/availability?person_id=${encodeURIComponent(pid)}`),
      get<BlackoutRow[]>(`/api/booking/blackouts?person_id=${encodeURIComponent(pid)}`),
      get<BookingRow[]>(`/api/booking/list?person_id=${encodeURIComponent(pid)}`),
    ]);
    bookingsCache = { me, availability: avail, blackouts: bls, bookings: bks, loaded: true };
  } catch (e) {
    bookingsCache.loaded = true;
    showToast('Failed to load bookings: ' + String((e as Error).message || e), 'err');
  }
  renderOverlay();
}

function renderAvailabilityRows(): void {
  const host = document.getElementById('us-bk-avail-list');
  if (!host) return;
  if (bookingsCache.availability.length === 0) {
    host.innerHTML = '<div class="us-hint">No windows yet. Add one below.</div>';
    return;
  }
  host.innerHTML = bookingsCache.availability.map((row, i) => `
    <div class="us-conn-row" data-avail-idx="${i}" style="display:grid;grid-template-columns: 80px 1fr 1fr auto;gap:8px;align-items:center">
      <select class="us-input us-bk-av-day" data-idx="${i}">
        ${DAY_NAMES.map((n, d) => `<option value="${d}" ${row.day_of_week === d ? 'selected' : ''}>${n}</option>`).join('')}
      </select>
      <input class="us-input us-input-mono us-bk-av-start" data-idx="${i}" value="${escAttr(minutesToHhmm(row.start_minute))}" placeholder="09:00">
      <input class="us-input us-input-mono us-bk-av-end" data-idx="${i}" value="${escAttr(minutesToHhmm(row.end_minute))}" placeholder="17:00">
      <button class="us-btn us-btn-danger us-btn-sm us-bk-av-del" data-idx="${i}" type="button">${iconTrash()}</button>
    </div>
  `).join('');

  host.querySelectorAll<HTMLButtonElement>('.us-bk-av-del').forEach(btn => {
    btn.addEventListener('click', () => {
      const idx = Number(btn.dataset['idx']);
      bookingsCache.availability.splice(idx, 1);
      renderAvailabilityRows();
    });
  });
}

function wireBookings(): void {
  if (!bookingsCache.loaded) return;
  const pid = state.ownerId || '';
  if (!pid) return;

  // ── Slug save ──
  document.getElementById('us-bk-slug-save')?.addEventListener('click', () => {
    const slug = (document.getElementById('us-bk-slug') as HTMLInputElement).value.trim().toLowerCase();
    void post<BookingMe>('/api/booking/me', { person_id: pid, slug }).then(me => {
      bookingsCache.me = me;
      showToast('Booking handle saved', 'ok');
      renderOverlay();
    }).catch(e => showToast(String((e as Error).message || e), 'err'));
  });

  document.getElementById('us-bk-copy')?.addEventListener('click', () => {
    const v = (document.getElementById('us-bk-link') as HTMLInputElement | null)?.value || '';
    if (!v) return;
    void navigator.clipboard.writeText(v).then(() => showToast('Link copied', 'ok'));
  });

  // ── Session details save ──
  document.getElementById('us-bk-session-save')?.addEventListener('click', () => {
    const session_minutes = Number((document.getElementById('us-bk-mins') as HTMLInputElement).value);
    const price_cents = Number((document.getElementById('us-bk-price') as HTMLInputElement).value);
    const currency = (document.getElementById('us-bk-currency') as HTMLSelectElement).value;
    const cancel_hours = Number((document.getElementById('us-bk-cancel') as HTMLInputElement).value);
    const blurb = (document.getElementById('us-bk-blurb') as HTMLTextAreaElement).value;
    void post<BookingMe>('/api/booking/me', {
      person_id: pid, session_minutes, price_cents, currency, cancel_hours, blurb,
    }).then(me => {
      bookingsCache.me = me;
      showToast('Session details saved', 'ok');
      renderOverlay();
    }).catch(e => showToast(String((e as Error).message || e), 'err'));
  });

  // ── Availability rows ──
  renderAvailabilityRows();

  document.getElementById('us-bk-avail-add')?.addEventListener('click', () => {
    bookingsCache.availability.push({
      id: '', person_id: pid, day_of_week: 1, start_minute: 9 * 60, end_minute: 17 * 60,
      timezone: bookingsCache.availability[0]?.timezone || 'Australia/Sydney', created_at: '',
    });
    renderAvailabilityRows();
  });

  document.getElementById('us-bk-avail-save')?.addEventListener('click', () => {
    // Read all row inputs fresh.
    const host = document.getElementById('us-bk-avail-list');
    if (!host) return;
    const rows: Array<{ day_of_week: number; start_minute: number; end_minute: number }> = [];
    const idxs = [...host.querySelectorAll<HTMLSelectElement>('.us-bk-av-day')].map(el => Number(el.dataset['idx']));
    for (const i of idxs) {
      const daySel = host.querySelector<HTMLSelectElement>(`.us-bk-av-day[data-idx="${i}"]`);
      const startEl = host.querySelector<HTMLInputElement>(`.us-bk-av-start[data-idx="${i}"]`);
      const endEl = host.querySelector<HTMLInputElement>(`.us-bk-av-end[data-idx="${i}"]`);
      if (!daySel || !startEl || !endEl) continue;
      const start = hhmmToMinutes(startEl.value);
      const end = hhmmToMinutes(endEl.value);
      if (start === null || end === null) { showToast(`Row ${i + 1}: bad time`, 'err'); return; }
      if (end <= start) { showToast(`Row ${i + 1}: end must be after start`, 'err'); return; }
      rows.push({ day_of_week: Number(daySel.value), start_minute: start, end_minute: end });
    }
    void post<AvailabilityRow[]>('/api/booking/availability', {
      person_id: pid,
      rows: rows.map(r => ({ ...r, timezone: bookingsCache.availability[0]?.timezone || 'Australia/Sydney' })),
    }).then(fresh => {
      bookingsCache.availability = fresh;
      showToast('Availability saved', 'ok');
      renderOverlay();
    }).catch(e => showToast(String((e as Error).message || e), 'err'));
  });

  // ── Blackouts ──
  overlayEl?.querySelectorAll<HTMLButtonElement>('[data-blackout-del]').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.dataset['blackoutDel'] || '';
      if (!id) return;
      confirmModal('Delete blackout', 'Delete this blackout?', () => {
        void post<unknown>(`/api/booking/blackouts/${encodeURIComponent(id)}/delete`, { person_id: pid }).then(() => {
          bookingsCache.blackouts = bookingsCache.blackouts.filter(b => b.id !== id);
          showToast('Blackout removed', 'ok');
          renderOverlay();
        }).catch(e => showToast(String((e as Error).message || e), 'err'));
      }, { confirmLabel: 'Delete', variant: 'danger' });
    });
  });

  document.getElementById('us-bk-bl-add')?.addEventListener('click', () => {
    const start = (document.getElementById('us-bk-bl-start') as HTMLInputElement).value.trim();
    const end = (document.getElementById('us-bk-bl-end') as HTMLInputElement).value.trim();
    const reason = (document.getElementById('us-bk-bl-reason') as HTMLInputElement).value.trim();
    if (!start || !end) { showToast('Start and end are required', 'err'); return; }
    void post<BlackoutRow>('/api/booking/blackouts', {
      person_id: pid, start_at: start, end_at: end, reason,
    }).then(row => {
      bookingsCache.blackouts.push(row);
      showToast('Blackout added', 'ok');
      renderOverlay();
    }).catch(e => showToast(String((e as Error).message || e), 'err'));
  });

  // ── Booking actions ──
  overlayEl?.querySelectorAll<HTMLButtonElement>('[data-bk-action]').forEach(btn => {
    btn.addEventListener('click', () => {
      const action = btn.dataset['bkAction'] || '';
      const id = btn.dataset['bkId'] || '';
      if (!id || !action) return;
      const run = (): void => {
        void post<BookingRow>(`/api/booking/${encodeURIComponent(id)}/${action}`, {
          person_id: pid,
        }).then(fresh => {
          bookingsCache.bookings = bookingsCache.bookings.map(b => b.id === id ? fresh : b);
          showToast(`Booking ${action}`, 'ok');
          renderOverlay();
        }).catch(e => showToast(String((e as Error).message || e), 'err'));
      };
      if (action === 'cancel') {
        confirmModal('Cancel booking', 'Cancel this booking?', run,
          { confirmLabel: 'Cancel booking', cancelLabel: 'Keep', variant: 'danger' });
      } else {
        run();
      }
    });
  });
}

// ─── Actions ───
function handleLogout(): void {
  confirmModal('Log out', 'Log out?', () => {
    document.cookie = 'hub_session=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    localStorage.clear();
    window.location.reload();
  }, { confirmLabel: 'Log Out', variant: 'danger' });
}

// ─── Helpers ───
function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}
function escAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}
// Keep the unused `iconCheck` import alive in case future sections use it.
void iconCheck;
